import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";

/** Draft-store step keys, now mapped 1:1 onto the generation flow:
 * 1 = Donesi (bring sources), 2 = Provjeri (review data + generate),
 * 3 = Dokumenti (download). Persisted drafts carry these same keys. */
export type StepKey = 1 | 2 | 3;

const STEPS: { key: StepKey; ordinal: number; label: string }[] = [
  { key: 1, ordinal: 1, label: "Donesi" },
  { key: 2, ordinal: 2, label: "Provjeri" },
  { key: 3, ordinal: 3, label: "Dokumenti" },
];

interface StepperProps {
  current: StepKey;
  /** Returns the route for a step, or null when it isn't reachable yet
   * (the user hasn't gotten that far). Reachable steps render as links
   * so the architect can hop back, fix a value, and regenerate. */
  hrefFor?: (step: StepKey) => string | null;
}

export function Stepper({ current, hrefFor }: StepperProps) {
  return (
    <div className="mx-auto mt-7 flex max-w-[360px] items-center px-6">
      {STEPS.map((step, idx) => {
        const isActive = step.key === current;
        const isDone = step.key < current;
        const href = !isActive ? hrefFor?.(step.key) ?? null : null;
        const inner = (
          <>
            <div
              className={cn(
                "flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 text-[0.8rem] font-bold transition-all",
                isActive && "border-ind-600 bg-ind-600 text-white",
                isDone && "border-green-500 bg-green-500 text-white",
                !isActive &&
                  !isDone &&
                  "border-stone-300 bg-white text-stone-400",
              )}
            >
              {step.ordinal}
            </div>
            <div
              className={cn(
                "whitespace-nowrap text-[0.78rem] font-semibold uppercase tracking-[0.04em]",
                isActive && "text-ind-700",
                isDone && "text-green-700",
                !isActive && !isDone && "text-stone-400",
              )}
            >
              {step.label}
            </div>
          </>
        );
        return (
          <span key={step.key} className="contents">
            {href ? (
              <Link
                to={href}
                className="group flex flex-shrink-0 items-center gap-2 no-underline transition-opacity hover:opacity-75"
                title={`Otvori korak ${step.ordinal} — ${step.label}`}
              >
                {inner}
              </Link>
            ) : (
              <div className="flex flex-shrink-0 items-center gap-2">{inner}</div>
            )}
            {idx < STEPS.length - 1 && (
              <div
                className={cn(
                  "mx-2 h-0.5 flex-1 min-w-[30px] transition-colors",
                  step.key < current ? "bg-green-500" : "bg-stone-200",
                )}
              />
            )}
          </span>
        );
      })}
    </div>
  );
}
